import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  IconButton,
  ListSubheader,
  Divider
} from '@mui/material';
import {
  Edit,
  ExpandMore,
  AccountBalance,
  TrendingUp,
  Assessment,
  Security,
  Description,
  Business
} from '@mui/icons-material';
import { api as axios } from '../../api';
import url from '../../serverUrl';
import { logoWrapper } from '../../utils';
import TreasuryLockStatusFields from './TreasuryLockStatusFields';

import {
  VENTURE_TYPE_CODES,
  RISK_TYPE_CODES,
  TREASURY_PROFILE_CODES
} from './venturesDictionary';

function EditVentureToken({ token, open, setOpen }) {
  /* ===================== STATE ===================== */
  const [form, setForm] = useState(token);
  const [status, setStatus] = useState(token?.status ?? false);
  const [logo, setLogo] = useState(token?.logo ?? '');

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    setForm(token);
    setStatus(token?.status ?? false);
    setLogo(token?.logo ?? '');
  }, [token]);

  /* ===================== EARLY RETURN ===================== */
  if (token?.type !== 'project') return null;

  /* ===================== HANDLERS ===================== */
  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleMultiSelectChange = (field) => (e) => {
    const value = e.target.value;
    setForm({
      ...form,
      [field]: typeof value === 'string' ? value.split(',') : value
    });
  };

  // API data isn't always shaped the way the form expects — these fields can
  // arrive as a JSON-encoded string, a comma-separated string, or an actual
  // array. MUI's multi-select (and .map() in renderValue) requires an array,
  // so normalize before it ever reaches the Select component.
  const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // not JSON, fall through to comma-split
      }
      return value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };

  const saveToken = () => {
    axios
      .put(`${url}/crypto/updateToken`, { ...form, status })
      .then((res) => alert(res.data.message || 'Token updated successfully'))
      .catch((err) => alert(JSON.stringify(err)));
  };

  const uploadLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('image', file);
    fd.append('fileName', file.name);

    axios.post(`${url}/hootdex/token-logo-upload`, fd).then((res) => {
      if (res.data.status === 'ok') {
        const img = `${url}/hootdex/images/${res.data.fileName}`;
        setLogo(img);
        setForm({ ...form, logo: img });
      }
    });
  };

  /* ===================== HELPER FUNCTIONS ===================== */

  // Get venture type options based on selected main type
  const getVentureTypeOptions = () => {
    const mainType = form?.venture_main_type;
    if (!mainType) return [];

    return VENTURE_TYPE_CODES.filter(
      (code) => code.visibleIf && code.visibleIf.equals === mainType
    );
  };

  // Get specific code by code number
  const getCodeByNumber = (codeNumber) => {
    return [
      ...VENTURE_TYPE_CODES,
      ...RISK_TYPE_CODES,
      ...TREASURY_PROFILE_CODES
    ].find((c) => c.code === codeNumber);
  };

  /* ===================== JSX ===================== */
  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight={600}>
            Edit Venture Token
          </Typography>
          <Chip
            label={status ? 'Active' : 'Inactive'}
            color={status ? 'success' : 'default'}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#f8f9fa', p: 0 }}>
        <Box p={3}>
          {/* Logo & Status Section */}
          <Paper
            elevation={0}
            sx={{ p: 3, mb: 3, bgcolor: 'white', borderRadius: 2 }}
          >
            <Box display="flex" alignItems="center" gap={3}>
              <Box position="relative">
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '2px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fafafa'
                  }}
                >
                  <img
                    src={logoWrapper(logo, token)}
                    width={100}
                    alt={form?.name}
                    style={{ objectFit: 'contain' }}
                  />
                </Box>
                <input
                  hidden
                  id="venture-logo"
                  type="file"
                  onChange={uploadLogo}
                />
                <label htmlFor="venture-logo">
                  <IconButton
                    component="span"
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 5,
                      right: 5,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </label>
              </Box>

              <Box flex={1}>
                <Typography variant="h6" gutterBottom>
                  {form?.name || 'Venture Token'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Symbol: {form?.symbol || 'N/A'}
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                  <Typography variant="body2" fontWeight={500}>
                    Status:
                  </Typography>
                  <Switch
                    checked={status}
                    onChange={(e) => setStatus(e.target.checked)}
                  />
                  <Chip
                    label={status ? 'Active' : 'Inactive'}
                    color={status ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* ========== ACCORDION SECTIONS ========== */}

          {/* Identity Section */}
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Description color="primary" />
                <Typography fontWeight={600}>Identity Information</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="Venture Token Name"
                  value={form?.name ?? ''}
                  onChange={handleChange('name')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Symbol"
                  value={form?.symbol ?? ''}
                  onChange={handleChange('symbol')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Identity Symbol"
                  value={form?.identitySymbol ?? ''}
                  disabled
                  size="small"
                  helperText="Auto-generated unique identifier"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Venture Classification Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Business color="primary" />
                <Typography fontWeight={600}>Venture Classification</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Main Token Type"
                  value={form?.venture_main_type}
                  onChange={handleChange('venture_main_type')}
                  options={[
                    {
                      code: 'Composite Asset Token',
                      label: 'Composite Asset Token'
                    },
                    {
                      code: 'Revenue-Linked Token',
                      label: 'Revenue-Linked Token'
                    },
                    {
                      code: 'Equity-Linked Token',
                      label: 'Equity-Linked Token'
                    },
                    { code: 'Debt-Linked Token', label: 'Debt-Linked Token' },
                    {
                      code: 'Project-Level Token',
                      label: 'Project-Level Token'
                    },
                    {
                      code: 'Commodity-Linked Token',
                      label: 'Commodity-Linked Token'
                    },
                    { code: 'Hybrid Token', label: 'Hybrid Token' }
                  ]}
                  size="small"
                />

                {form?.venture_main_type && (
                  <SelectBlock
                    label="Specific Venture Type"
                    value={form?.venture_type_code}
                    onChange={handleChange('venture_type_code')}
                    options={getVentureTypeOptions().map((v) => ({
                      code: v.code,
                      label: v.label
                    }))}
                    size="small"
                    helperText={`Available options for ${form.venture_main_type}`}
                  />
                )}

                {/* Conditional Multi-Select for Equity/Debt tokens */}
                {(form?.venture_main_type === 'Equity-Linked Token' ||
                  form?.venture_main_type === 'Debt-Linked Token') && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Custody Type (Multi-Select)</InputLabel>
                    <Select
                      multiple
                      value={toArray(form?.custody_types)}
                      onChange={handleMultiSelectChange('custody_types')}
                      label="Custody Type (Multi-Select)"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      <MenuItem value="Transfer Agent">
                        Custody Type: Transfer Agent
                      </MenuItem>
                      <MenuItem value="Broker-Dealer">
                        Custody Type: Broker-Dealer
                      </MenuItem>
                      <MenuItem value="Custodial Bank">
                        Custody Type: Custodial Bank
                      </MenuItem>
                      <MenuItem value="Clearing Agency">
                        Custody Type: Clearing Agency
                      </MenuItem>
                      <MenuItem value="Trust Company">
                        Custody Type: Trust Company
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Economic Terms Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp color="primary" />
                <Typography fontWeight={600}>Economic Terms</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Current Price"
                    value={form?.currentPrice ?? ''}
                    onChange={handleChange('currentPrice')}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Volume"
                    value={form?.volume ?? ''}
                    onChange={handleChange('volume')}
                    size="small"
                  />
                </Box>
                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Face Value"
                    value={form?.face_value ?? ''}
                    onChange={handleChange('face_value')}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Token Amount"
                    value={form?.token_amount ?? ''}
                    onChange={handleChange('token_amount')}
                    size="small"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Issuer / Company Name"
                  value={form?.issuer ?? ''}
                  onChange={handleChange('issuer')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Currency"
                  value={form?.currency ?? 'USD'}
                  onChange={handleChange('currency')}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Treasury Configuration Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <AccountBalance color="primary" />
                <Typography fontWeight={600}>Treasury Configuration</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <TreasuryLockStatusFields tokenType="PROJECT" tokenId={form?.id} />
                <SelectBlock
                  label="Treasury Type"
                  value={form?.treasury_type}
                  onChange={handleChange('treasury_type')}
                  options={
                    TREASURY_PROFILE_CODES.find(
                      (c) => c.code === '132199'
                    )?.options?.map((opt) => ({
                      code: opt,
                      label: opt
                    })) || []
                  }
                  grouped={false}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Risk Profile Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Security color="primary" />
                <Typography fontWeight={600}>Risk Profile</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Issuer Credit Grade"
                  value={form?.credit_grade}
                  onChange={handleChange('credit_grade')}
                  options={
                    RISK_TYPE_CODES.find(
                      (c) => c.code === '135101'
                    )?.options?.map((opt) => ({
                      code: opt,
                      label: opt
                    })) || []
                  }
                  size="small"
                />
                <SelectBlock
                  label="Default Risk Score"
                  value={form?.default_risk}
                  onChange={handleChange('default_risk')}
                  options={
                    RISK_TYPE_CODES.find(
                      (c) => c.code === '135102'
                    )?.options?.map((opt) => ({
                      code: opt,
                      label: opt
                    })) || []
                  }
                  size="small"
                />
                <SelectBlock
                  label="Liquidity Risk Score"
                  value={form?.liquidity_risk}
                  onChange={handleChange('liquidity_risk')}
                  options={
                    RISK_TYPE_CODES.find(
                      (c) => c.code === '135103'
                    )?.options?.map((opt) => ({
                      code: opt,
                      label: opt
                    })) || []
                  }
                  size="small"
                />
                <SelectBlock
                  label="Operational Risk Score"
                  value={form?.operational_risk}
                  onChange={handleChange('operational_risk')}
                  options={
                    RISK_TYPE_CODES.find(
                      (c) => c.code === '135104'
                    )?.options?.map((opt) => ({
                      code: opt,
                      label: opt
                    })) || []
                  }
                  size="small"
                />
                <SelectBlock
                  label="Smart-Contract Risk Score"
                  value={form?.smart_contract_risk}
                  onChange={handleChange('smart_contract_risk')}
                  options={
                    RISK_TYPE_CODES.find(
                      (c) => c.code === '135105'
                    )?.options?.map((opt) => ({
                      code: opt,
                      label: opt
                    })) || []
                  }
                  size="small"
                />
                <SelectBlock
                  label="DAT Collateralization Level"
                  value={form?.collateralization_level}
                  onChange={handleChange('collateralization_level')}
                  options={
                    RISK_TYPE_CODES.find(
                      (c) => c.code === '135106'
                    )?.options?.map((opt) => ({
                      code: opt,
                      label: opt
                    })) || []
                  }
                  size="small"
                />
                <SelectBlock
                  label="Asset Coverage Ratio"
                  value={form?.asset_coverage_ratio}
                  onChange={handleChange('asset_coverage_ratio')}
                  options={
                    RISK_TYPE_CODES.find(
                      (c) => c.code === '135107'
                    )?.options?.map((opt) => ({
                      code: opt,
                      label: opt
                    })) || []
                  }
                  size="small"
                />
                <SelectBlock
                  label="Concentration Risk"
                  value={form?.concentration_risk}
                  onChange={handleChange('concentration_risk')}
                  options={
                    RISK_TYPE_CODES.find(
                      (c) => c.code === '135108'
                    )?.options?.map((opt) => ({
                      code: opt,
                      label: opt
                    })) || []
                  }
                  size="small"
                />
                <SelectBlock
                  label="Regulatory Risk Level"
                  value={form?.regulatory_risk}
                  onChange={handleChange('regulatory_risk')}
                  options={
                    RISK_TYPE_CODES.find(
                      (c) => c.code === '135109'
                    )?.options?.map((opt) => ({
                      code: opt,
                      label: opt
                    })) || []
                  }
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Project Information Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Assessment color="primary" />
                <Typography fontWeight={600}>Project Information</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="Project Name"
                  value={form?.project_name ?? ''}
                  onChange={handleChange('project_name')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Project Stage"
                  value={form?.project_stage ?? ''}
                  onChange={handleChange('project_stage')}
                  size="small"
                  helperText="e.g., Pre-Revenue, Growth, Mature"
                />
                <TextField
                  fullWidth
                  label="Project Location"
                  value={form?.project_location ?? ''}
                  onChange={handleChange('project_location')}
                  size="small"
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Revenue Model (Multi-Select)</InputLabel>
                  <Select
                    multiple
                    value={toArray(form?.revenue_model)}
                    onChange={handleMultiSelectChange('revenue_model')}
                    label="Revenue Model (Multi-Select)"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Subscription">
                      Subscription (Recurring Revenue)
                    </MenuItem>
                    <MenuItem value="Transaction Fees">
                      Transaction Fees
                    </MenuItem>
                    <MenuItem value="Licensing">Licensing</MenuItem>
                    <MenuItem value="Usage-Based">
                      Usage-Based / Metered
                    </MenuItem>
                    <MenuItem value="SaaS">SaaS / Platform Fees</MenuItem>
                    <MenuItem value="Asset Sales">Asset Sales</MenuItem>
                    <MenuItem value="Leasing">Leasing / Rental</MenuItem>
                    <MenuItem value="Advertising">Advertising</MenuItem>
                    <MenuItem value="Data Monetization">
                      Data Monetization
                    </MenuItem>
                    <MenuItem value="Token Utility">
                      Token-Based Utility
                    </MenuItem>
                    <MenuItem value="Yield">Yield / Interest Spread</MenuItem>
                    <MenuItem value="Royalty">Royalty-Based</MenuItem>
                    <MenuItem value="Performance Fees">
                      Performance-Based Fees
                    </MenuItem>
                    <MenuItem value="Marketplace">
                      Marketplace Commissions
                    </MenuItem>
                    <MenuItem value="Contracts">Long-Term Contracts</MenuItem>
                    <MenuItem value="Offtake">Offtake Agreements</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>
                    Project Risk Assessment (Multi-Select)
                  </InputLabel>
                  <Select
                    multiple
                    value={toArray(form?.project_risks)}
                    onChange={handleMultiSelectChange('project_risks')}
                    label="Project Risk Assessment (Multi-Select)"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Very Low Risk">Very Low Risk</MenuItem>
                    <MenuItem value="Low Risk">Low Risk</MenuItem>
                    <MenuItem value="Moderate Risk">Moderate Risk</MenuItem>
                    <MenuItem value="Elevated Risk">Elevated Risk</MenuItem>
                    <MenuItem value="High Risk">High Risk</MenuItem>
                    <MenuItem value="Speculative">Speculative</MenuItem>
                    <MenuItem value="Development Risk">
                      Development Risk
                    </MenuItem>
                    <MenuItem value="Regulatory Risk">Regulatory Risk</MenuItem>
                    <MenuItem value="Market Adoption Risk">
                      Market Adoption Risk
                    </MenuItem>
                    <MenuItem value="Technology Risk">Technology Risk</MenuItem>
                    <MenuItem value="Capital Risk">Capital Risk</MenuItem>
                    <MenuItem value="Execution Risk">Execution Risk</MenuItem>
                    <MenuItem value="Geopolitical Risk">
                      Geopolitical Risk
                    </MenuItem>
                    <MenuItem value="Commodity Risk">
                      Commodity / Price Risk
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Additional Information Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Description color="primary" />
                <Typography fontWeight={600}>Additional Information</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="External Link"
                  value={form?.link ?? ''}
                  onChange={handleChange('link')}
                  size="small"
                  helperText="Website or documentation URL"
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Sort Order"
                  value={form?.sort ?? ''}
                  onChange={handleChange('sort')}
                  size="small"
                  helperText="Display order in lists"
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Description"
                  value={form?.description ?? ''}
                  onChange={handleChange('description')}
                  helperText="Detailed description of the venture token"
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ bgcolor: 'white', px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}
      >
        <Button onClick={() => setOpen(false)} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={saveToken}
          sx={{
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
            px: 4
          }}
        >
          Save Venture Token
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ===================== HELPERS ===================== */

const SelectBlock = ({
  label,
  value,
  onChange,
  options,
  grouped = false,
  size = 'medium',
  helperText
}) => {
  if (!grouped) {
    return (
      <FormControl fullWidth size={size}>
        <InputLabel>{label}</InputLabel>
        <Select value={value ?? ''} label={label} onChange={onChange}>
          {options.map((o) => (
            <MenuItem key={o.code} value={o.code}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, ml: 1.5 }}
          >
            {helperText}
          </Typography>
        )}
      </FormControl>
    );
  }

  // Group options by category for better organization
  const groupedOptions = options.reduce((acc, option) => {
    const category = option.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(option);
    return acc;
  }, {});

  return (
    <FormControl fullWidth size={size}>
      <InputLabel>{label}</InputLabel>
      <Select value={value ?? ''} label={label} onChange={onChange}>
        {Object.entries(groupedOptions).map(([category, items]) => [
          <ListSubheader
            key={`header-${category}`}
            sx={{ fontWeight: 600, color: 'primary.main' }}
          >
            {category}
          </ListSubheader>,
          ...items.map((o) => (
            <MenuItem key={o.code} value={o.code} sx={{ pl: 4 }}>
              <Typography variant="body2">{o.label}</Typography>
            </MenuItem>
          ))
        ])}
      </Select>
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, ml: 1.5 }}
        >
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default EditVentureToken;
